import { supabase } from './supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import * as XLSX from 'xlsx'
// Import papaparse via wrapper to handle UMD module
import Papa from '../utils/papaparse-wrapper'

/**
 * Export clients to CSV
 */
export const exportClientsToCSV = async (organizationId, selectedIds = null) => {
  try {
    let query = supabase
      .from('clients')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })

    if (selectedIds && selectedIds.length > 0) {
      query = query.in('id', selectedIds)
    }

    const { data, error } = await query

    if (error) throw error

    // Map to CSV-friendly format
    const csvData = (data || []).map((client) => ({
      'First Name': client.first_name,
      'Last Name': client.last_name,
      'Email': client.email || '',
      'Phone': client.phone || '',
      'Date of Birth': client.date_of_birth || '',
      'Gender': client.gender || '',
      'Created At': client.created_at,
      'Updated At': client.updated_at,
    }))

    // Papa is imported synchronously, so we can use it directly
    const csv = Papa.unparse(csvData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `clients-export-${Date.now()}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    return { success: true, count: csvData.length }
  } catch (error) {
    console.error('Error exporting clients to CSV:', error)
    throw error
  }
}

/**
 * Export clients to Excel
 */
export const exportClientsToExcel = async (organizationId, selectedIds = null) => {
  try {
    let query = supabase
      .from('clients')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })

    if (selectedIds && selectedIds.length > 0) {
      query = query.in('id', selectedIds)
    }

    const { data, error } = await query

    if (error) throw error

    // Map to Excel-friendly format
    const excelData = (data || []).map((client) => ({
      'First Name': client.first_name,
      'Last Name': client.last_name,
      'Email': client.email || '',
      'Phone': client.phone || '',
      'Date of Birth': client.date_of_birth || '',
      'Gender': client.gender || '',
      'Created At': client.created_at,
      'Updated At': client.updated_at,
    }))

    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.json_to_sheet(excelData)
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients')

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `clients-export-${Date.now()}.xlsx`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    return { success: true, count: excelData.length }
  } catch (error) {
    console.error('Error exporting clients to Excel:', error)
    throw error
  }
}

/**
 * Parse CSV/Excel file
 */
export const parseImportFile = async (file) => {
  return new Promise((resolve, reject) => {
    const fileExtension = file.name.split('.').pop().toLowerCase()

    if (fileExtension === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          resolve(results.data)
        },
        error: (error) => {
          reject(error)
        },
      })
    } else if (['xlsx', 'xls'].includes(fileExtension)) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet)
          resolve(jsonData)
        } catch (error) {
          reject(error)
        }
      }
      reader.onerror = (error) => reject(error)
      reader.readAsArrayBuffer(file)
    } else {
      reject(new Error('Unsupported file format. Please use CSV or Excel files.'))
    }
  })
}

/**
 * Map column headers to client fields
 */
export const mapColumnsToFields = (headers, columnMapping) => {
  const mapping = {}
  headers.forEach((header, index) => {
    if (columnMapping[header]) {
      mapping[columnMapping[header]] = index
    }
  })
  return mapping
}

/**
 * Import clients from CSV/Excel
 */
export const importClients = async (organizationId, file, columnMapping, options = {}) => {
  try {
    // Parse file
    const rawData = await parseImportFile(file)

    if (!rawData || rawData.length === 0) {
      throw new Error('File is empty or could not be parsed')
    }

    // Get headers from first row
    const headers = Object.keys(rawData[0])

    // Validate required mappings
    const requiredFields = ['first_name', 'last_name']
    const missingFields = requiredFields.filter((field) => !columnMapping[field])
    if (missingFields.length > 0) {
      throw new Error(`Missing required column mappings: ${missingFields.join(', ')}`)
    }

    // Map data to client format
    const clients = rawData
      .map((row, index) => {
        const client = {
          organization_id: organizationId,
        }

        // Map each column
        Object.entries(columnMapping).forEach(([field, columnName]) => {
          if (columnName && row[columnName] !== undefined && row[columnName] !== null && row[columnName] !== '') {
            client[field] = String(row[columnName]).trim()
          }
        })

        // Validate required fields
        if (!client.first_name || !client.last_name) {
          return null // Skip invalid rows
        }

        return client
      })
      .filter((client) => client !== null)

    if (clients.length === 0) {
      throw new Error('No valid client records found in file')
    }

    // Import clients
    const results = {
      total: rawData.length,
      valid: clients.length,
      invalid: rawData.length - clients.length,
      imported: 0,
      errors: [],
    }

    // Batch insert (Supabase allows up to 1000 rows per insert)
    const batchSize = 100
    for (let i = 0; i < clients.length; i += batchSize) {
      const batch = clients.slice(i, i + batchSize)
      const { data, error } = await supabase.from('clients').insert(batch).select()

      if (error) {
        results.errors.push({
          batch: Math.floor(i / batchSize) + 1,
          error: error.message,
        })
      } else {
        results.imported += data?.length || 0
      }
    }

    return results
  } catch (error) {
    console.error('Error importing clients:', error)
    throw error
  }
}

/**
 * Detect column mappings automatically
 */
export const detectColumnMappings = (headers) => {
  const mappings = {}
  const fieldVariations = {
    first_name: ['first name', 'firstname', 'fname', 'given name', 'first'],
    last_name: ['last name', 'lastname', 'lname', 'surname', 'family name', 'last'],
    email: ['email', 'e-mail', 'email address'],
    phone: ['phone', 'phone number', 'telephone', 'mobile', 'cell'],
    date_of_birth: ['date of birth', 'dob', 'birthdate', 'birth date', 'date of birth'],
    gender: ['gender', 'sex'],
  }

  headers.forEach((header) => {
    const normalizedHeader = header.toLowerCase().trim()
    Object.entries(fieldVariations).forEach(([field, variations]) => {
      if (variations.some((variation) => normalizedHeader.includes(variation))) {
        mappings[field] = header
      }
    })
  })

  return mappings
}

/**
 * Get available column fields for mapping
 */
export const getAvailableFields = () => {
  return [
    { value: 'first_name', label: 'First Name', required: true },
    { value: 'last_name', label: 'Last Name', required: true },
    { value: 'email', label: 'Email', required: false },
    { value: 'phone', label: 'Phone', required: false },
    { value: 'date_of_birth', label: 'Date of Birth', required: false },
    { value: 'gender', label: 'Gender', required: false },
  ]
}

